import React, { useEffect, useState, useRef, Fragment } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { Encounter, Patient, ClinicalNote, LabRequest, RadiologyRequest, Prescription } from '../../types';
import { Stethoscope, FileText, FlaskConical, Scan, Pill, CheckCircle, History, X, ZoomIn, ZoomOut, Eye, Printer, Download } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { fbcStructure, urinalysisTests, testosteroneTests, isFBCTest, isUrinalysisTest, isTestosteroneTest } from '../../store/labStructures';
type CViewingTest = {
  kind: 'lab';
  labId: string;
  testName?: string;
} | {
  kind: 'rad';
  radId: string;
} | null;
export const ClinicianDashboard: React.FC = () => {
  const {
    encounters,
    patients,
    updateEncounter,
    currentUser,
    getPatientEncounters
  } = useData();
  const [selectedEncounterId, setSelectedEncounterId] = useState<string | null>(null);
  // Live encounter - always reads from context so lab/rad updates appear instantly
  const selectedEncounter = selectedEncounterId ? encounters.find(e => e.id === selectedEncounterId) || null : null;
  // Image zoom state
  const [zoomImage, setZoomImage] = useState<string | null>(null);
  // View Test File modal state
  const [viewingTest, setViewingTest] = useState<CViewingTest>(null);
  const [testImageZoom, setTestImageZoom] = useState<number>(100);
  const [isGeneratingTestPdf, setIsGeneratingTestPdf] = useState(false);
  const testModalRef = useRef<HTMLDivElement>(null);
  const closeTestModal = () => {
    setViewingTest(null);
    setTestImageZoom(100);
  };
  const handlePrintTestModal = () => {
    document.body.classList.add('printing-test-modal');
    window.print();
    setTimeout(() => {
      document.body.classList.remove('printing-test-modal');
    }, 500);
  };
  const handleDownloadTestPdf = async () => {
    if (!testModalRef.current) return;
    setIsGeneratingTestPdf(true);
    try {
      const filename = `BMED_TestResult_${Date.now()}.pdf`;
      const pageWidthCm = 21.0;
      const pageHeightCm = 29.7;
      const marginCm = 1.5;
      const contentWidthCm = pageWidthCm - marginCm * 2;
      const contentHeightCm = pageHeightCm - marginCm * 2;
      const canvas = await html2canvas(testModalRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false
      });
      const pdf = new jsPDF({
        unit: 'cm',
        format: 'a4',
        orientation: 'portrait',
        compress: true
      });
      const imgPropsHeightCm = canvas.height * contentWidthCm / canvas.width;
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      if (imgPropsHeightCm <= contentHeightCm) {
        pdf.addImage(imgData, 'JPEG', marginCm, marginCm, contentWidthCm, imgPropsHeightCm);
      } else {
        let remainingHeightCm = imgPropsHeightCm;
        let positionCm = marginCm;
        while (remainingHeightCm > 0) {
          pdf.addImage(imgData, 'JPEG', marginCm, positionCm, contentWidthCm, imgPropsHeightCm);
          remainingHeightCm -= contentHeightCm;
          if (remainingHeightCm > 0) {
            pdf.addPage();
            positionCm = marginCm - (imgPropsHeightCm - remainingHeightCm);
          }
        }
      }
      pdf.save(filename);
    } catch (err) {
      console.error('Test PDF failed', err);
      alert('Failed to generate PDF.');
    } finally {
      setIsGeneratingTestPdf(false);
    }
  };
  // Modals state
  const [showLabModal, setShowLabModal] = useState(false);
  const [showRadModal, setShowRadModal] = useState(false);
  const [showRxModal, setShowRxModal] = useState(false);
  // Clinical Notes State
  const [notes, setNotes] = useState<Partial<ClinicalNote>>({
    presentingComplaint: '',
    historyOfPresentingComplaint: '',
    pastMedicalHistory: [],
    drugHistory: '',
    familyMedicalHistory: [],
    obgynHistory: '',
    socialHistory: {
      alcohol: '',
      smoking: '',
      occupation: '',
      mentalStatus: ''
    },
    reviewOfSystems: {
      rs: '',
      cvs: '',
      cns: '',
      git: '',
      gut: '',
      mss: '',
      is: ''
    },
    onExamination: '',
    significantFindings: [],
    workingDiagnosis: []
  });
  // Lab Request State
  const [selectedTests, setSelectedTests] = useState<string[]>([]);
  const [otherTests, setOtherTests] = useState('');
  // Rad Request State
  const [scanType, setScanType] = useState('');
  // Rx State
  const [rxForm, setRxForm] = useState({
    drugName: '',
    dose: '',
    route: '',
    frequency: '',
    days: ''
  });
  const pendingEncounters = encounters.filter(e => e.stage === 'CLINICIAN' || e.stage === 'INVESTIGATIONS' && !e.pendingLab && !e.pendingRadiology);
  const getPatient = (patientId: string) => patients.find(p => p.id === patientId);
  const handleSelectEncounter = (encounter: Encounter) => {
    setSelectedEncounterId(encounter.id);
    if (encounter.clinicalNote) {
      setNotes(encounter.clinicalNote);
    } else {
      setNotes({
        presentingComplaint: '',
        historyOfPresentingComplaint: '',
        pastMedicalHistory: [],
        drugHistory: '',
        familyMedicalHistory: [],
        obgynHistory: '',
        socialHistory: {
          alcohol: '',
          smoking: '',
          occupation: '',
          mentalStatus: ''
        },
        reviewOfSystems: {
          rs: '',
          cvs: '',
          cns: '',
          git: '',
          gut: '',
          mss: '',
          is: ''
        },
        onExamination: '',
        significantFindings: [],
        workingDiagnosis: []
      });
    }
    // Move encounter back to CLINICIAN stage if returning from investigations
    if (encounter.stage === 'INVESTIGATIONS') {
      updateEncounter(encounter.id, {
        stage: 'CLINICIAN'
      });
    }
  };
  const handleSaveNotes = () => {
    if (!selectedEncounter || !currentUser) return;
    const clinicalNote: ClinicalNote = {
      ...(notes as ClinicalNote),
      recordedAt: new Date().toISOString(),
      recordedBy: currentUser.id,
      clinicianName: currentUser.fullName
    };
    updateEncounter(selectedEncounter.id, {
      clinicalNote,
      lastAttendingClinician: currentUser.fullName
    });
    alert('Clinical notes saved successfully.');
  };
  const handleRequestLab = () => {
    if (!selectedEncounter || !currentUser || selectedTests.length === 0 && !otherTests) return;
    const newLab: LabRequest = {
      id: `lab-${Date.now()}`,
      tests: selectedTests,
      otherTests,
      requestedAt: new Date().toISOString(),
      requestedBy: currentUser.id,
      status: 'Pending'
    };
    updateEncounter(selectedEncounter.id, {
      labRequests: [...selectedEncounter.labRequests, newLab],
      stage: 'INVESTIGATIONS',
      pendingLab: true
    });
    setShowLabModal(false);
    setSelectedTests([]);
    setOtherTests('');
    alert('Laboratory tests requested.');
  };
  const handleRequestRad = () => {
    if (!selectedEncounter || !currentUser || !scanType) return;
    const newRad: RadiologyRequest = {
      id: `rad-${Date.now()}`,
      scanType,
      requestedAt: new Date().toISOString(),
      requestedBy: currentUser.id,
      status: 'Pending'
    };
    updateEncounter(selectedEncounter.id, {
      radiologyRequests: [...selectedEncounter.radiologyRequests, newRad],
      stage: 'INVESTIGATIONS',
      pendingRadiology: true
    });
    setShowRadModal(false);
    setScanType('');
    alert('Radiology scan requested.');
  };
  const handleAddRx = () => {
    if (!selectedEncounter || !currentUser || !rxForm.drugName) return;
    const newRx: Prescription = {
      id: `rx-${Date.now()}`,
      ...rxForm,
      status: 'Pending',
      prescribedAt: new Date().toISOString(),
      prescribedBy: currentUser.id
    };
    updateEncounter(selectedEncounter.id, {
      prescriptions: [...selectedEncounter.prescriptions, newRx]
    });
    setRxForm({
      drugName: '',
      dose: '',
      route: '',
      frequency: '',
      days: ''
    });
  };
  const handleCompleteVisit = () => {
    if (!selectedEncounter || !currentUser) return;
    // Save notes one last time
    const clinicalNote: ClinicalNote = {
      ...(notes as ClinicalNote),
      recordedAt: new Date().toISOString(),
      recordedBy: currentUser.id,
      clinicianName: currentUser.fullName
    };
    const hasPrescriptions = selectedEncounter.prescriptions.length > 0;
    const hasPendingDispense = selectedEncounter.prescriptions.some(rx => rx.status === 'Pending');
    // If prescriptions exist and none have been dispensed yet, send to pharmacy.
    // If pharmacy has already processed them (returned to clinician), complete the visit.
    const sendToPharmacy = hasPrescriptions && hasPendingDispense;
    updateEncounter(selectedEncounter.id, {
      clinicalNote,
      lastAttendingClinician: currentUser.fullName,
      stage: sendToPharmacy ? 'PHARMACY' : 'COMPLETED',
      completedAt: sendToPharmacy ? undefined : new Date().toISOString()
    });
    alert(sendToPharmacy ? 'Visit saved. Patient forwarded to Pharmacy.' : 'Visit completed successfully.');
    setSelectedEncounterId(null);
  };
  const handleSendToPharmacy = () => {
    if (!selectedEncounter || !currentUser) return;
    const pendingRx = selectedEncounter.prescriptions.filter(rx => rx.status === 'Pending');
    if (pendingRx.length === 0) {
      alert('No pending prescriptions to send to pharmacy.');
      return;
    }
    // Save current notes
    const clinicalNote: ClinicalNote = {
      ...(notes as ClinicalNote),
      recordedAt: new Date().toISOString(),
      recordedBy: currentUser.id,
      clinicianName: currentUser.fullName
    };
    updateEncounter(selectedEncounter.id, {
      clinicalNote,
      lastAttendingClinician: currentUser.fullName,
      stage: 'PHARMACY'
    });
    alert(`Prescription sent to Pharmacy (${pendingRx.length} drug${pendingRx.length > 1 ? 's' : ''}).`);
    setSelectedEncounterId(null);
  };
  if (selectedEncounter) {
    const patient = getPatient(selectedEncounter.patientId);
    if (!patient) return <div data-id="element-605">Patient not found</div>;
    return <div className="space-y-6 pb-20" data-id="element-606">
        {/* Sticky Action Bar */}
        <div className="flex justify-between items-center sticky top-0 bg-gray-100 z-10 py-4 border-b border-gray-200" data-id="element-607">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-608">Consultation</h1>
          <div className="flex space-x-2" data-id="element-609">
            <Button variant="outline" onClick={() => setSelectedEncounterId(null)} data-id="element-610">
              Close File
            </Button>
            <Button variant="primary" onClick={handleSaveNotes} data-id="element-611">
              Save Draft
            </Button>
            <Button variant="success" className="bg-green-600 text-white hover:bg-green-700" onClick={handleCompleteVisit} data-id="element-612">
              <CheckCircle className="w-4 h-4 mr-2" data-id="element-613" /> Complete Visit
            </Button>
          </div>
        </div>

        {/* Medical Form */}
        <div className="bg-white border-2 border-gray-300 rounded-lg p-6" data-id="element-614">
          {/* ═══ FORM HEADER ═══ */}
          <div className="flex justify-between items-start mb-6" data-id="element-615">
            <div className="flex items-center" data-id="element-616">
              <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED Medical Centre" className="h-20 w-20 object-contain mr-4" data-id="element-617" />
              <div data-id="element-618">
                <h1 className="text-2xl font-bold text-pink-600 uppercase tracking-wider" data-id="element-619">
                  BMED Medical Centre
                </h1>
                <p className="text-sm italic text-gray-600" data-id="element-620">
                  Maternity Care Made Better
                </p>
              </div>
            </div>
            <div className="text-right" data-id="element-621">
              <p className="font-bold text-lg uppercase tracking-widest border-b-2 border-black pb-1 mb-2" data-id="element-622">
                Confidential
              </p>
              <p className="text-sm" data-id="element-623">
                <span className="font-semibold" data-id="element-624">Date:</span>{' '}
                {new Date(selectedEncounter.startedAt).toLocaleDateString()}
              </p>
              <p className="text-sm" data-id="element-625">
                <span className="font-semibold" data-id="element-626">File No:</span>{' '}
                {patient.bmcFileNumber}
              </p>
            </div>
          </div>

          {/* ═══ PERSONAL DETAILS ═══ */}
          <div className="border-2 border-black p-4 mb-6 text-sm space-y-3" data-id="element-627">
            <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-628">
              <div className="col-span-1 font-semibold" data-id="element-629">1.</div>
              <div className="col-span-6" data-id="element-630">
                <span className="font-semibold" data-id="element-631">Full Names:</span>{' '}
                {patient.firstName} {patient.lastName}
              </div>
              <div className="col-span-3" data-id="element-632">
                <span className="font-semibold" data-id="element-633">Sex:</span> {patient.sex}
              </div>
              <div className="col-span-2" data-id="element-634">
                <span className="font-semibold" data-id="element-635">Age:</span> {patient.age}
              </div>
            </div>
            <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-636">
              <div className="col-span-1 font-semibold" data-id="element-637">2.</div>
              <div className="col-span-11" data-id="element-638">
                <span className="font-semibold" data-id="element-639">Marital Status:</span>{' '}
                {patient.maritalStatus}
              </div>
            </div>
            <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-640">
              <div className="col-span-1 font-semibold" data-id="element-641">3.</div>
              <div className="col-span-7" data-id="element-642">
                <span className="font-semibold" data-id="element-643">Home Address:</span>{' '}
                {patient.address}
              </div>
              <div className="col-span-4" data-id="element-644">
                <span className="font-semibold" data-id="element-645">Contact:</span>{' '}
                {patient.contact}
              </div>
            </div>
            <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-646">
              <div className="col-span-1 font-semibold" data-id="element-647">4.</div>
              <div className="col-span-11" data-id="element-648">
                <span className="font-semibold" data-id="element-649">Occupation:</span>{' '}
                {patient.occupation || '_________________________'}
              </div>
            </div>
            <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-650">
              <div className="col-span-1 font-semibold" data-id="element-651">5.</div>
              <div className="col-span-5" data-id="element-652">
                <span className="font-semibold" data-id="element-653">Next of Kin:</span>{' '}
                {patient.nextOfKin}
              </div>
              <div className="col-span-3" data-id="element-654">
                <span className="font-semibold" data-id="element-655">Contact:</span>{' '}
                {patient.nokContact}
              </div>
              <div className="col-span-3" data-id="element-656">
                <span className="font-semibold" data-id="element-657">Relationship:</span>{' '}
                {patient.nokRelationship}
              </div>
            </div>
            <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-658">
              <div className="col-span-1 font-semibold" data-id="element-659">6.</div>
              <div className="col-span-11" data-id="element-660">
                <span className="font-semibold" data-id="element-661">Allergies:</span>{' '}
                <span className="text-red-600 font-medium" data-id="element-662">
                  {patient.allergies}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-12 gap-2" data-id="element-663">
              <div className="col-span-1 font-semibold" data-id="element-664">7.</div>
              <div className="col-span-11" data-id="element-665">
                <span className="font-semibold" data-id="element-666">HIV Status:</span>{' '}
                <span className="ml-4" data-id="element-667">
                  <span className={`inline-block px-2 py-0.5 border border-black mr-3 ${patient.hivStatus === 'Positive' ? 'bg-red-100 font-bold' : ''}`} data-id="element-668">
                    Positive {patient.hivStatus === 'Positive' ? '✓' : ''}
                  </span>
                  <span className={`inline-block px-2 py-0.5 border border-black mr-3 ${patient.hivStatus === 'Negative' ? 'bg-green-100 font-bold' : ''}`} data-id="element-669">
                    Negative {patient.hivStatus === 'Negative' ? '✓' : ''}
                  </span>
                  <span className={`inline-block px-2 py-0.5 border border-black ${patient.hivStatus === 'Unknown' ? 'bg-yellow-100 font-bold' : ''}`} data-id="element-670">
                    Unknown {patient.hivStatus === 'Unknown' ? '✓' : ''}
                  </span>
                </span>
              </div>
            </div>
          </div>

          {/* ═══ NURSING OBSERVATIONS ═══ */}
          <div className="mb-6" data-id="element-671">
            <h3 className="font-bold uppercase bg-gray-200 p-2 border-2 border-black border-b-0 text-center" data-id="element-672">
              Nursing Observations
            </h3>
            <table className="w-full border-collapse border-2 border-black text-sm text-center" data-id="element-673">
              <thead data-id="element-674">
                <tr className="bg-gray-50" data-id="element-675">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => <th key={n} className="border border-black p-1 text-xs text-gray-500" data-id="element-676">
                      {n}
                    </th>)}
                </tr>
                <tr className="bg-gray-100" data-id="element-677">
                  <th className="border border-black p-1" data-id="element-678">Temp</th>
                  <th className="border border-black p-1" data-id="element-679">Pulse</th>
                  <th className="border border-black p-1" data-id="element-680">Resp</th>
                  <th className="border border-black p-1" data-id="element-681">BP</th>
                  <th className="border border-black p-1" data-id="element-682">SpO2</th>
                  <th className="border border-black p-1" data-id="element-683">Weight (Kg)</th>
                  <th className="border border-black p-1" data-id="element-684">Height</th>
                  <th className="border border-black p-1" data-id="element-685">BMI</th>
                  <th className="border border-black p-1" data-id="element-686">Pain Score</th>
                  <th className="border border-black p-1" data-id="element-687">AVPU</th>
                </tr>
              </thead>
              <tbody data-id="element-688">
                <tr data-id="element-689">
                  <td className="border border-black p-2 font-medium" data-id="element-690">
                    {selectedEncounter.vitals?.temp || ''}
                  </td>
                  <td className="border border-black p-2 font-medium" data-id="element-691">
                    {selectedEncounter.vitals?.pulse || ''}
                  </td>
                  <td className="border border-black p-2 font-medium" data-id="element-692">
                    {selectedEncounter.vitals?.resp || ''}
                  </td>
                  <td className="border border-black p-2 font-medium" data-id="element-693">
                    {selectedEncounter.vitals?.bp || ''}
                  </td>
                  <td className="border border-black p-2 font-medium" data-id="element-694">
                    {selectedEncounter.vitals?.sp02 || ''}
                  </td>
                  <td className="border border-black p-2 font-medium" data-id="element-695">
                    {selectedEncounter.vitals?.weight || ''}
                  </td>
                  <td className="border border-black p-2 font-medium" data-id="element-696">
                    {selectedEncounter.vitals?.height || ''}
                  </td>
                  <td className="border border-black p-2 font-medium" data-id="element-697">
                    {selectedEncounter.vitals?.bmi || ''}
                  </td>
                  <td className="border border-black p-2 font-medium" data-id="element-698">
                    {selectedEncounter.vitals?.painScore || ''}
                  </td>
                  <td className="border border-black p-2 font-medium" data-id="element-699">
                    {selectedEncounter.vitals?.avpu || ''}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* ═══ STAT DOSES ADMINISTERED BY NURSING ═══ */}
          {selectedEncounter.statDoses && selectedEncounter.statDoses.length > 0 && <div className="border-2 border-black mb-6" data-id="element-700">
                <h3 className="font-bold uppercase bg-yellow-100 p-2 border-b border-black text-center text-sm" data-id="element-701">
                  ⚡ Stat Doses Administered by Nursing
                </h3>
                <table className="w-full border-collapse text-sm" data-id="element-702">
                  <thead data-id="element-703">
                    <tr className="bg-gray-100" data-id="element-704">
                      <th className="border border-black p-1.5 w-10" data-id="element-705">No.</th>
                      <th className="border border-black p-1.5 text-left" data-id="element-706">
                        Drug Name
                      </th>
                      <th className="border border-black p-1.5 text-left" data-id="element-707">
                        Dose
                      </th>
                      <th className="border border-black p-1.5 text-left" data-id="element-708">
                        Route
                      </th>
                      <th className="border border-black p-1.5 text-left" data-id="element-709">
                        Time Given
                      </th>
                      <th className="border border-black p-1.5 text-left" data-id="element-710">
                        Nurse
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-711">
                    {selectedEncounter.statDoses.map((dose, idx) => <tr key={dose.id} data-id="element-712">
                        <td className="border border-black p-1.5 text-center" data-id="element-713">
                          {idx + 1}
                        </td>
                        <td className="border border-black p-1.5 font-medium" data-id="element-714">
                          {dose.drugName}
                        </td>
                        <td className="border border-black p-1.5" data-id="element-715">
                          {dose.dose}
                        </td>
                        <td className="border border-black p-1.5" data-id="element-716">
                          {dose.route}
                        </td>
                        <td className="border border-black p-1.5" data-id="element-717">
                          {dose.givenAt ? new Date(dose.givenAt).toLocaleString() : ''}
                        </td>
                        <td className="border border-black p-1.5 italic text-xs" data-id="element-718">
                          {dose.nurseName || ''}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>}

          {/* ═══ CLINICAL NOTES ═══ */}
          <div className="space-y-5 mb-6 text-sm" data-id="element-719">
            {/* PRESENTING COMPLAINT */}
            <div className="border-2 border-black" data-id="element-720">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-721">
                Presenting Complaint
              </h3>
              <textarea className="w-full p-3 min-h-[100px] focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.presentingComplaint} onChange={e => setNotes({
              ...notes,
              presentingComplaint: e.target.value
            })} data-id="element-722" />
            </div>

            {/* HISTORY OF PRESENTING COMPLAINT */}
            <div className="border-2 border-black" data-id="element-723">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-724">
                History of Presenting Complaint
              </h3>
              <textarea className="w-full p-3 min-h-[100px] focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.historyOfPresentingComplaint} onChange={e => setNotes({
              ...notes,
              historyOfPresentingComplaint: e.target.value
            })} data-id="element-725" />
            </div>

            {/* PAST MEDICAL HISTORY */}
            <div className="border-2 border-black" data-id="element-726">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-727">
                Past Medical History / General Co-Morbidities
              </h3>
              <table className="w-full border-collapse text-sm" data-id="element-728">
                <tbody data-id="element-729">
                  {['DIABETES', 'EPILEPSY', 'TUBERCULOSIS', 'HYPERTENSION', 'ASTHMA', 'SICKLE CELL DISEASE', 'ANEMIA', 'RVD'].map(condition => <tr key={condition} className="border-b border-gray-300" data-id="element-730">
                      <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-731">
                        {condition}
                      </td>
                      <td className="p-2 text-center" data-id="element-732">
                        <label className="flex items-center justify-center cursor-pointer" data-id="element-733">
                          <input type="checkbox" className="mr-2 h-4 w-4" checked={notes.pastMedicalHistory?.includes(condition)} onChange={e => {
                        const current = notes.pastMedicalHistory || [];
                        if (e.target.checked) {
                          setNotes({
                            ...notes,
                            pastMedicalHistory: [...current, condition]
                          });
                        } else {
                          setNotes({
                            ...notes,
                            pastMedicalHistory: current.filter(c => c !== condition)
                          });
                        }
                      }} data-id="element-734" />
                          {notes.pastMedicalHistory?.includes(condition) ? 'Yes' : ''}
                        </label>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>

            {/* DRUG HISTORY */}
            <div className="border-2 border-black" data-id="element-735">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-736">
                Drug History
              </h3>
              <textarea className="w-full p-3 min-h-[60px] focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.drugHistory} onChange={e => setNotes({
              ...notes,
              drugHistory: e.target.value
            })} data-id="element-737" />
              <div className="p-3 border-t border-gray-300 bg-gray-50" data-id="element-738">
                <span className="font-semibold" data-id="element-739">Any known allergy:</span>{' '}
                <span className="text-red-600 font-medium" data-id="element-740">
                  {patient.allergies}
                </span>
              </div>
            </div>

            {/* FAMILY MEDICAL HISTORY */}
            <div className="border-2 border-black" data-id="element-741">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-742">
                Family Medical History
              </h3>
              <table className="w-full border-collapse text-sm" data-id="element-743">
                <tbody data-id="element-744">
                  {['DIABETES', 'EPILEPSY', 'TUBERCULOSIS', 'HYPERTENSION', 'ASTHMA', 'SICKLE CELL DISEASE', 'ANEMIA'].map(condition => <tr key={condition} className="border-b border-gray-300" data-id="element-745">
                      <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-746">
                        {condition}
                      </td>
                      <td className="p-2 text-center" data-id="element-747">
                        <label className="flex items-center justify-center cursor-pointer" data-id="element-748">
                          <input type="checkbox" className="mr-2 h-4 w-4" checked={notes.familyMedicalHistory?.includes(condition)} onChange={e => {
                        const current = notes.familyMedicalHistory || [];
                        if (e.target.checked) {
                          setNotes({
                            ...notes,
                            familyMedicalHistory: [...current, condition]
                          });
                        } else {
                          setNotes({
                            ...notes,
                            familyMedicalHistory: current.filter(c => c !== condition)
                          });
                        }
                      }} data-id="element-749" />
                          {notes.familyMedicalHistory?.includes(condition) ? 'Yes' : ''}
                        </label>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>

            {/* OBSTETRICS AND GYNAECOLOGY HISTORY */}
            <div className="border-2 border-black" data-id="element-750">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-751">
                Obstetrics and Gynaecology History
              </h3>
              <textarea className="w-full p-3 min-h-[80px] focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.obgynHistory} onChange={e => setNotes({
              ...notes,
              obgynHistory: e.target.value
            })} data-id="element-752" />
            </div>

            {/* SOCIAL HISTORY */}
            <div className="border-2 border-black" data-id="element-753">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-754">
                Social History
              </h3>
              <table className="w-full border-collapse text-sm" data-id="element-755">
                <tbody data-id="element-756">
                  <tr className="border-b border-gray-300" data-id="element-757">
                    <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-758">
                      Alcohol
                    </td>
                    <td className="p-2" data-id="element-759">
                      <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.socialHistory?.alcohol || ''} onChange={e => setNotes({
                      ...notes,
                      socialHistory: {
                        ...notes.socialHistory,
                        alcohol: e.target.value
                      }
                    })} data-id="element-760" />
                    </td>
                  </tr>
                  <tr className="border-b border-gray-300" data-id="element-761">
                    <td className="border-r border-gray-300 p-2 font-medium" data-id="element-762">
                      Smoking
                    </td>
                    <td className="p-2" data-id="element-763">
                      <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.socialHistory?.smoking || ''} onChange={e => setNotes({
                      ...notes,
                      socialHistory: {
                        ...notes.socialHistory,
                        smoking: e.target.value
                      }
                    })} data-id="element-764" />
                    </td>
                  </tr>
                  <tr className="border-b border-gray-300" data-id="element-765">
                    <td className="border-r border-gray-300 p-2 font-medium" data-id="element-766">
                      Occupation
                    </td>
                    <td className="p-2" data-id="element-767">
                      <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.socialHistory?.occupation || ''} onChange={e => setNotes({
                      ...notes,
                      socialHistory: {
                        ...notes.socialHistory,
                        occupation: e.target.value
                      }
                    })} data-id="element-768" />
                    </td>
                  </tr>
                  <tr data-id="element-769">
                    <td className="border-r border-gray-300 p-2 font-medium" data-id="element-770">
                      Mental Status
                    </td>
                    <td className="p-2" data-id="element-771">
                      <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.socialHistory?.mentalStatus || ''} onChange={e => setNotes({
                      ...notes,
                      socialHistory: {
                        ...notes.socialHistory,
                        mentalStatus: e.target.value
                      }
                    })} data-id="element-772" />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* REVIEW OF SYSTEMS */}
            <div className="border-2 border-black" data-id="element-773">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-774">
                Review of Systems
              </h3>
              <div className="p-3 space-y-2" data-id="element-775">
                {[{
                key: 'rs',
                label: 'R/S:'
              }, {
                key: 'cvs',
                label: 'CVS:'
              }, {
                key: 'cns',
                label: 'CNS:'
              }, {
                key: 'git',
                label: 'GIT:'
              }, {
                key: 'gut',
                label: 'GUT:'
              }, {
                key: 'mss',
                label: 'MSS:'
              }, {
                key: 'is',
                label: 'IS:'
              }].map(system => <div key={system.key} className="flex items-center" data-id="element-776">
                    <span className="font-semibold w-16" data-id="element-777">{system.label}</span>
                    <input type="text" className="flex-1 p-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.reviewOfSystems?.[system.key as keyof typeof notes.reviewOfSystems] || ''} onChange={e => setNotes({
                  ...notes,
                  reviewOfSystems: {
                    ...notes.reviewOfSystems,
                    [system.key]: e.target.value
                  }
                })} data-id="element-778" />
                  </div>)}
              </div>
            </div>

            {/* ON EXAMINATION */}
            <div className="border-2 border-black" data-id="element-779">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-780">
                On Examination
              </h3>
              <textarea className="w-full p-3 min-h-[120px] focus:ring-2 focus:ring-blue-500 focus:outline-none" placeholder="G/C – Stable / Ill Looking / Unconscious&#10;- Febrile / Afebrile / Lethargic&#10;- P|J|C&#10;&#10;S1  S2 : -&#10;&#10;R/S  : -&#10;&#10;P/A" value={notes.onExamination} onChange={e => setNotes({
              ...notes,
              onExamination: e.target.value
            })} data-id="element-781" />
            </div>

            {/* SUMMARY OF SIGNIFICANT FINDINGS */}
            <div className="border-2 border-black" data-id="element-782">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-783">
                Summary of Significant Findings
              </h3>
              <div className="p-3 space-y-2" data-id="element-784">
                {[0, 1, 2, 3, 4, 5, 6, 7].map(i => <div key={i} className="flex items-start" data-id="element-785">
                    <span className="font-medium mr-2 mt-1" data-id="element-786">{i + 1}.</span>
                    <input type="text" className="flex-1 p-1 border-b border-gray-300 focus:border-blue-500 focus:outline-none" value={notes.significantFindings?.[i] || ''} onChange={e => {
                  const current = notes.significantFindings || Array(8).fill('');
                  const updated = [...current];
                  updated[i] = e.target.value;
                  setNotes({
                    ...notes,
                    significantFindings: updated
                  });
                }} data-id="element-787" />
                  </div>)}
              </div>
            </div>

            {/* WORKING DIAGNOSIS */}
            <div className="border-2 border-black" data-id="element-788">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-789">
                Working Diagnosis
              </h3>
              <div className="p-3 space-y-2" data-id="element-790">
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map(i => <div key={i} className="flex items-start" data-id="element-791">
                    <span className="font-medium mr-2 mt-1" data-id="element-792">{i + 1}.</span>
                    <input type="text" className="flex-1 p-1 border-b border-gray-300 focus:border-blue-500 focus:outline-none" value={notes.workingDiagnosis?.[i] || ''} onChange={e => {
                  const current = notes.workingDiagnosis || Array(10).fill('');
                  const updated = [...current];
                  updated[i] = e.target.value;
                  setNotes({
                    ...notes,
                    workingDiagnosis: updated
                  });
                }} data-id="element-793" />
                  </div>)}
              </div>
            </div>

            {/* INVESTIGATIONS TO BE DONE */}
            <div className="border-2 border-black" data-id="element-794">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-795">
                Investigations To Be Done
              </h3>
              <div className="p-4" data-id="element-796">
                <table className="w-full border-collapse text-sm mb-4" data-id="element-797">
                  <tbody data-id="element-798">
                    {[['FBC', 'GRAVINDEX'], ['MPS/RDT', 'HVS'], ['CREATINE / U&A', 'URINALYSIS'], ['LIVER FUNCTION TEST', 'PAP SMEAR'], ['AAFB', 'CD4 COUNT'], ['URINE MICROSCOPY', 'URETHRAL SWAB'], ['VBG', 'VIRAL LOAD'], ['HEPATITIS PROFILE', 'RBS / FBS'], ['ABG', 'STOOL MICROSCOPY'], ['LIVER FUNCTION TEST', 'DOA 6'], ['CXR', 'RPR'], ['HIV', 'SCAN/ULTRA SOUND'], ['SPERM ANALYSIS', 'TESTOSTERONE']].map((pair, idx) => <tr key={idx} className="border-b border-gray-200" data-id="element-799">
                        {pair.map(test => {
                      const isOrdered = selectedEncounter.labRequests.some(lab => lab.tests.includes(test)) || test === 'SCAN/ULTRA SOUND' && selectedEncounter.radiologyRequests.length > 0;
                      return <Fragment key={test} data-id="element-800">
                              <td className="p-1.5 font-medium border-r border-gray-200 w-1/3" data-id="element-801">
                                {test}
                              </td>
                              <td className="p-1.5 w-1/6 text-center border-r border-gray-300" data-id="element-802">
                                {isOrdered ? <span className="text-green-700 font-bold" data-id="element-803">
                                    ✓
                                  </span> : ''}
                              </td>
                            </Fragment>;
                    })}
                      </tr>)}
                  </tbody>
                </table>

                <div className="flex gap-3 mb-4" data-id="element-804">
                  <Button variant="outline" onClick={() => setShowLabModal(true)} className="flex-1" data-id="element-805">
                    <FlaskConical className="w-4 h-4 mr-2" data-id="element-806" /> Request Laboratory
                    Test
                  </Button>
                  <Button variant="outline" onClick={() => setShowRadModal(true)} className="flex-1 border-purple-600 text-purple-600 hover:bg-purple-50" data-id="element-807">
                    <Scan className="w-4 h-4 mr-2" data-id="element-808" /> Request Radiology Scan
                  </Button>
                </div>

                <div className="border-t border-gray-300 pt-2" data-id="element-809">
                  <span className="font-semibold" data-id="element-810">Other investigations:</span>{' '}
                  {selectedEncounter.labRequests.map(l => l.otherTests).filter(Boolean).join(', ') || ''}
                </div>

                {/* Investigation Results — View File per test */}
                {(selectedEncounter.labRequests.some(l => l.status === 'Completed') || selectedEncounter.radiologyRequests.some(r => r.status === 'Completed')) && <div className="mt-4 pt-4 border-t-2 border-black" data-id="element-811">
                    <h4 className="font-bold mb-3 uppercase text-center" data-id="element-812">
                      Investigation Results
                    </h4>
                    <table className="w-full border-collapse text-sm" data-id="element-813">
                      <thead data-id="element-814">
                        <tr className="bg-gray-100" data-id="element-815">
                          <th className="border border-black p-1.5 text-left" data-id="element-816">
                            Test / Scan
                          </th>
                          <th className="border border-black p-1.5 text-left" data-id="element-817">
                            Type
                          </th>
                          <th className="border border-black p-1.5 text-left" data-id="element-818">
                            Performed By
                          </th>
                          <th className="border border-black p-1.5 text-left" data-id="element-819">
                            Completed
                          </th>
                          <th className="border border-black p-1.5 text-center w-32" data-id="element-820">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody data-id="element-821">
                        {selectedEncounter.labRequests.filter(l => l.status === 'Completed').flatMap(lab => lab.tests.map(testName => <tr key={`${lab.id}-${testName}`} data-id="element-822">
                                <td className="border border-black p-1.5 font-medium" data-id="element-823">
                                  {testName}
                                </td>
                                <td className="border border-black p-1.5" data-id="element-824">
                                  Laboratory
                                </td>
                                <td className="border border-black p-1.5" data-id="element-825">—</td>
                                <td className="border border-black p-1.5 text-xs" data-id="element-826">
                                  {lab.completedAt ? new Date(lab.completedAt).toLocaleString() : ''}
                                </td>
                                <td className="border border-black p-1.5 text-center" data-id="element-827">
                                  <Button size="sm" variant="outline" onClick={() => setViewingTest({
                          kind: 'lab',
                          labId: lab.id,
                          testName
                        })} data-id="element-828">
                                    <Eye className="w-3.5 h-3.5 mr-1" data-id="element-829" /> View
                                    File
                                  </Button>
                                </td>
                              </tr>))}
                        {selectedEncounter.labRequests.filter(l => l.status === 'Completed' && l.otherTests).map(lab => <tr key={`${lab.id}-other`} data-id="element-830">
                              <td className="border border-black p-1.5 font-medium" data-id="element-831">
                                {lab.otherTests}{' '}
                                <span className="text-xs text-gray-500" data-id="element-832">
                                  (Other)
                                </span>
                              </td>
                              <td className="border border-black p-1.5" data-id="element-833">
                                Laboratory
                              </td>
                              <td className="border border-black p-1.5" data-id="element-834">—</td>
                              <td className="border border-black p-1.5 text-xs" data-id="element-835">
                                {lab.completedAt ? new Date(lab.completedAt).toLocaleString() : ''}
                              </td>
                              <td className="border border-black p-1.5 text-center" data-id="element-836">
                                <Button size="sm" variant="outline" onClick={() => setViewingTest({
                          kind: 'lab',
                          labId: lab.id,
                          testName: lab.otherTests
                        })} data-id="element-837">
                                  <Eye className="w-3.5 h-3.5 mr-1" data-id="element-838" /> View File
                                </Button>
                              </td>
                            </tr>)}
                        {selectedEncounter.radiologyRequests.filter(r => r.status === 'Completed').map(rad => <tr key={rad.id} data-id="element-839">
                              <td className="border border-black p-1.5 font-medium" data-id="element-840">
                                {rad.scanType}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-841">
                                Radiology
                              </td>
                              <td className="border border-black p-1.5" data-id="element-842">
                                {rad.radiographerName || '—'}
                              </td>
                              <td className="border border-black p-1.5 text-xs" data-id="element-843">
                                {rad.completedAt ? new Date(rad.completedAt).toLocaleString() : ''}
                              </td>
                              <td className="border border-black p-1.5 text-center" data-id="element-844">
                                <Button size="sm" variant="outline" onClick={() => setViewingTest({
                          kind: 'rad',
                          radId: rad.id
                        })} data-id="element-845">
                                  <Eye className="w-3.5 h-3.5 mr-1" data-id="element-846" /> View File
                                </Button>
                              </td>
                            </tr>)}
                      </tbody>
                    </table>
                  </div>}
              </div>
            </div>

            {/* DRUG TREATMENT / PRESCRIPTION */}
            <div className="border-2 border-black" data-id="element-847">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-848">
                Drug Treatment / Prescription
              </h3>
              <table className="w-full border-collapse text-sm text-center" data-id="element-849">
                <thead data-id="element-850">
                  <tr className="bg-gray-100" data-id="element-851">
                    <th className="border border-black p-1.5 w-10" data-id="element-852">No.</th>
                    <th className="border border-black p-1.5 text-left" data-id="element-853">
                      Drug Name
                    </th>
                    <th className="border border-black p-1.5" data-id="element-854">Dose</th>
                    <th className="border border-black p-1.5" data-id="element-855">Route</th>
                    <th className="border border-black p-1.5" data-id="element-856">Frequency</th>
                    <th className="border border-black p-1.5" data-id="element-857">No. of Days</th>
                    <th className="border border-black p-1.5" data-id="element-858">Signature</th>
                  </tr>
                </thead>
                <tbody data-id="element-859">
                  {selectedEncounter.prescriptions.length > 0 ? selectedEncounter.prescriptions.map((rx, i) => <tr key={rx.id} className={rx.status === 'Dispensed' ? 'bg-green-50' : rx.status === 'Not Dispensed' ? 'bg-red-50' : ''} data-id="element-860">
                          <td className="border border-black p-1.5" data-id="element-861">{i + 1}</td>
                          <td className="border border-black p-1.5 text-left font-medium" data-id="element-862">
                            {rx.drugName}
                          </td>
                          <td className="border border-black p-1.5" data-id="element-863">
                            {rx.dose}
                          </td>
                          <td className="border border-black p-1.5" data-id="element-864">
                            {rx.route}
                          </td>
                          <td className="border border-black p-1.5" data-id="element-865">
                            {rx.frequency}
                          </td>
                          <td className="border border-black p-1.5" data-id="element-866">
                            {rx.days}
                          </td>
                          <td className="border border-black p-1.5 text-xs" data-id="element-867">
                            {rx.status === 'Dispensed' && <span className="text-green-700 font-bold" data-id="element-868">
                                ✓ Dispensed
                              </span>}
                            {rx.status === 'Not Dispensed' && <span className="text-red-700 font-bold" data-id="element-869">
                                ✗ Not Available
                              </span>}
                            {rx.status === 'Pending' && <span className="italic text-gray-500" data-id="element-870">
                                Pending
                              </span>}
                            {rx.pharmacistName && <div className="text-gray-500 italic mt-0.5" data-id="element-871">
                                {rx.pharmacistName}
                              </div>}
                          </td>
                        </tr>) : [1, 2, 3, 4, 5, 6, 7].map(i => <tr key={i} data-id="element-872">
                          <td className="border border-black p-3" data-id="element-873">{i}</td>
                          <td className="border border-black p-3" data-id="element-874"></td>
                          <td className="border border-black p-3" data-id="element-875"></td>
                          <td className="border border-black p-3" data-id="element-876"></td>
                          <td className="border border-black p-3" data-id="element-877"></td>
                          <td className="border border-black p-3" data-id="element-878"></td>
                          <td className="border border-black p-3" data-id="element-879"></td>
                        </tr>)}
                </tbody>
              </table>
              <div className="p-3 border-t border-gray-300 flex gap-3" data-id="element-880">
                <Button variant="outline" onClick={() => setShowRxModal(true)} className="flex-1 text-green-600 border-green-600 hover:bg-green-50" data-id="element-881">
                  <Pill className="w-4 h-4 mr-2" data-id="element-882" /> Prescribe Medication
                </Button>
                {selectedEncounter.prescriptions.some(rx => rx.status === 'Pending') && <Button onClick={handleSendToPharmacy} className="flex-1 bg-teal-600 hover:bg-teal-700 text-white" data-id="element-883">
                    <Pill className="w-4 h-4 mr-2" data-id="element-884" /> Send Prescription to
                    Pharmacy
                  </Button>}
              </div>
            </div>

            {/* FOLLOW UP / REVIEW DATE */}
            <div className="border-2 border-black" data-id="element-885">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-886">
                Next Follow Up / Review Date (If Any)
              </h3>
              <table className="w-full border-collapse text-sm text-center" data-id="element-887">
                <thead data-id="element-888">
                  <tr className="bg-gray-100" data-id="element-889">
                    <th className="border border-black p-1 w-10" data-id="element-890">No.</th>
                    <th className="border border-black p-1" data-id="element-891">Date</th>
                    <th className="border border-black p-1 w-10" data-id="element-892">No.</th>
                    <th className="border border-black p-1" data-id="element-893">Date</th>
                    <th className="border border-black p-1 w-10" data-id="element-894">No.</th>
                    <th className="border border-black p-1" data-id="element-895">Date</th>
                  </tr>
                </thead>
                <tbody data-id="element-896">
                  {[0, 1, 2, 3, 4].map(row => <tr key={row} data-id="element-897">
                      <td className="border border-black p-1 font-medium" data-id="element-898">
                        {row + 1}
                      </td>
                      <td className="border border-black p-1" data-id="element-899">
                        {row === 0 ? <input type="date" className="w-full p-1 text-center focus:ring-2 focus:ring-blue-500 focus:outline-none" value={selectedEncounter.reviewDate ? new Date(selectedEncounter.reviewDate).toISOString().split('T')[0] : ''} onChange={e => {
                      if (e.target.value) {
                        updateEncounter(selectedEncounter.id, {
                          reviewDate: new Date(e.target.value).toISOString()
                        });
                      }
                    }} data-id="element-900" /> : ''}
                      </td>
                      <td className="border border-black p-1 font-medium" data-id="element-901">
                        {row + 6}
                      </td>
                      <td className="border border-black p-2" data-id="element-902"></td>
                      <td className="border border-black p-1 font-medium" data-id="element-903">
                        {row + 11}
                      </td>
                      <td className="border border-black p-2" data-id="element-904"></td>
                    </tr>)}
                </tbody>
              </table>
            </div>

            {/* NAME OF MEDICAL PRACTITIONER */}
            <div className="border-2 border-black p-4 mt-6" data-id="element-905">
              <p className="font-bold text-sm" data-id="element-906">
                Name of the Medical Practitioner:{' '}
                <span className="font-normal border-b border-black inline-block min-w-[250px] ml-2" data-id="element-907">
                  {currentUser?.fullName || ''}
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* ═══ MODALS ═══ */}
        {/* Lab Modal */}
        {showLabModal && <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50" data-id="element-908">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col" data-id="element-909">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-910">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-911">
                  Request Laboratory Tests
                </h3>
                <button onClick={() => setShowLabModal(false)} data-id="element-912">
                  <X className="w-5 h-5 text-gray-400" data-id="element-913" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto flex-1" data-id="element-914">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6" data-id="element-915">
                  <div data-id="element-916">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-917">
                      HAEMATOLOGY
                    </h4>
                    {['Full Blood Count', 'Blood Group', 'CD4'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-918">
                        <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-919" />{' '}
                        {test}
                      </label>)}
                  </div>
                  <div data-id="element-920">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-921">
                      CHEMISTRY
                    </h4>
                    {['Urea', 'Creatinine', 'FBS', 'RBS'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-922">
                        <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-923" />{' '}
                        {test}
                      </label>)}
                  </div>
                  <div data-id="element-924">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-925">
                      SEROLOGY
                    </h4>
                    {['HIV', 'Syphilis', 'HBs Ag', 'HCV'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-926">
                        <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-927" />{' '}
                        {test}
                      </label>)}
                  </div>
                </div>
                <div className="mt-6" data-id="element-928">
                  <Input label="Other Tests (Free Text)" value={otherTests} onChange={e => setOtherTests(e.target.value)} placeholder="Enter any other tests required..." data-id="element-929" />
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 bg-gray-50" data-id="element-930">
                <Button variant="outline" onClick={() => setShowLabModal(false)} data-id="element-931">
                  Cancel
                </Button>
                <Button onClick={handleRequestLab} data-id="element-932">Send to Laboratory</Button>
              </div>
            </div>
          </div>}

        {/* Rad Modal */}
        {showRadModal && <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50" data-id="element-933">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full" data-id="element-934">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-935">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-936">
                  Request Radiology Scan
                </h3>
                <button onClick={() => setShowRadModal(false)} data-id="element-937">
                  <X className="w-5 h-5 text-gray-400" data-id="element-938" />
                </button>
              </div>
              <div className="p-6" data-id="element-939">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-940">
                  Scan Type <span className="text-red-500" data-id="element-941">*</span>
                </label>
                <select required value={scanType} onChange={e => setScanType(e.target.value)} className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm" data-id="element-942">
                  <option value="" data-id="element-943">— Select a scan —</option>
                  <option value="Pelvic Scan" data-id="element-944">Pelvic Scan</option>
                  <option value="Anomaly Scan" data-id="element-945">Anomaly Scan</option>
                  <option value="OBS Scan" data-id="element-946">OBS Scan</option>
                </select>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 bg-gray-50" data-id="element-947">
                <Button variant="outline" onClick={() => setShowRadModal(false)} data-id="element-948">
                  Cancel
                </Button>
                <Button onClick={handleRequestRad} className="bg-purple-600 hover:bg-purple-700" data-id="element-949">
                  Send to Radiology
                </Button>
              </div>
            </div>
          </div>}

        {/* Rx Modal */}
        {showRxModal && <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50" data-id="element-950">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full" data-id="element-951">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-952">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-953">
                  Prescribe Medication
                </h3>
                <button onClick={() => setShowRxModal(false)} data-id="element-954">
                  <X className="w-5 h-5 text-gray-400" data-id="element-955" />
                </button>
              </div>
              <div className="p-6" data-id="element-956">
                <div className="grid grid-cols-2 gap-4" data-id="element-957">
                  <Input label="Drug Name" className="col-span-2" value={rxForm.drugName} onChange={e => setRxForm({
                ...rxForm,
                drugName: e.target.value
              })} required data-id="element-958" />
                  <Input label="Dose" placeholder="e.g. 500mg" value={rxForm.dose} onChange={e => setRxForm({
                ...rxForm,
                dose: e.target.value
              })} required data-id="element-959" />
                  <Select label="Route" options={[{
                value: 'PO',
                label: 'Oral (PO)'
              }, {
                value: 'IV',
                label: 'Intravenous (IV)'
              }, {
                value: 'IM',
                label: 'Intramuscular (IM)'
              }, {
                value: 'Topical',
                label: 'Topical'
              }]} value={rxForm.route} onChange={e => setRxForm({
                ...rxForm,
                route: e.target.value
              })} data-id="element-960" />
                  <Input label="Frequency" placeholder="e.g. BD, TDS" value={rxForm.frequency} onChange={e => setRxForm({
                ...rxForm,
                frequency: e.target.value
              })} required data-id="element-961" />
                  <Input label="No. of Days" type="number" value={rxForm.days} onChange={e => setRxForm({
                ...rxForm,
                days: e.target.value
              })} required data-id="element-962" />
                </div>
                <div className="mt-4 flex justify-end" data-id="element-963">
                  <Button onClick={handleAddRx} variant="outline" className="text-green-600 border-green-600 hover:bg-green-50" data-id="element-964">
                    + Add to Prescription List
                  </Button>
                </div>

                {selectedEncounter.prescriptions.length > 0 && <div className="mt-6 border-t border-gray-200 pt-4" data-id="element-965">
                    <h4 className="font-medium text-sm mb-2" data-id="element-966">Current List:</h4>
                    <ul className="space-y-2" data-id="element-967">
                      {selectedEncounter.prescriptions.map(rx => <li key={rx.id} className="text-sm bg-gray-50 p-2 rounded flex justify-between" data-id="element-968">
                          <span data-id="element-969">
                            {rx.drugName} - {rx.dose}
                          </span>
                          <span className="text-gray-500" data-id="element-970">
                            {rx.frequency} x {rx.days} days
                          </span>
                        </li>)}
                    </ul>
                  </div>}
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 bg-gray-50" data-id="element-971">
                <Button onClick={() => setShowRxModal(false)} data-id="element-972">Done</Button>
              </div>
            </div>
          </div>}

        {/* Image Zoom Modal */}
        {zoomImage && <div className="fixed inset-0 bg-black bg-opacity-90 flex flex-col items-center justify-center z-50" onClick={() => setZoomImage(null)} data-id="element-973">
            <div className="absolute top-4 right-4 flex items-center gap-3 z-10" data-id="element-974">
              <span className="text-white text-sm bg-black bg-opacity-50 px-3 py-1 rounded" data-id="element-975">
                Scroll to zoom • Click outside to close
              </span>
              <button className="text-white hover:text-gray-300 bg-black bg-opacity-50 rounded-full p-2" onClick={() => setZoomImage(null)} data-id="element-976">
                <X className="w-6 h-6" data-id="element-977" />
              </button>
            </div>
            <div className="max-w-[90vw] max-h-[85vh] overflow-auto rounded-lg" onClick={e => e.stopPropagation()} data-id="element-978">
              <img src={zoomImage} alt="Radiology Scan — Zoom up to 200%" className="min-w-[60vw] max-w-none" style={{
            width: '180%',
            cursor: 'grab'
          }} data-id="element-979" />
            </div>
            <p className="text-gray-400 text-xs mt-3" data-id="element-980">
              Image displayed at up to 200% zoom. Scroll within the frame to
              pan.
            </p>
          </div>}

        {/* ─── TEST FILE MODAL (View File per investigation) ─── */}
        {viewingTest && selectedEncounter && (() => {
        const patient = getPatient(selectedEncounter.patientId);
        if (!patient) return null;
        const lab = viewingTest.kind === 'lab' ? selectedEncounter.labRequests.find(l => l.id === viewingTest.labId) : null;
        const rad = viewingTest.kind === 'rad' ? selectedEncounter.radiologyRequests.find(r => r.id === viewingTest.radId) : null;
        if (!lab && !rad) return null;
        const focusedTest = viewingTest.kind === 'lab' ? viewingTest.testName : null;
        // Filter lab.results by the requested test name when possible
        const labResultsFiltered = lab && lab.results ? focusedTest ? Object.fromEntries(Object.entries(lab.results).filter(([k]) => k.toLowerCase().includes(focusedTest.toLowerCase()) || focusedTest.toLowerCase().includes(k.toLowerCase()) ||
        // FBC sub-tests, urinalysis (UA_), testosterone — keep all if focused test is one of these umbrella tests
        ['fbc', 'urinalysis', 'testosterone', 'sperm analysis'].some(u => focusedTest.toLowerCase().includes(u)))) : lab.results : null;
        return <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 print:p-0 print:bg-white" data-id="element-981">
                <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-y-auto print:max-h-none print:max-w-none print:shadow-none print:rounded-none print:overflow-visible test-modal-print" data-id="element-982">
                  <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-3 flex justify-between items-center z-10 print:hidden" data-id="element-983">
                    <h3 className="text-lg font-semibold text-gray-900" data-id="element-984">
                      {lab ? `Laboratory Result — ${focusedTest || lab.tests.join(', ')}` : `Radiology Result — ${rad?.scanType}`}
                    </h3>
                    <div className="flex items-center space-x-2" data-id="element-985">
                      <Button variant="outline" size="sm" onClick={handlePrintTestModal} data-id="element-986">
                        <Printer className="w-4 h-4 mr-1" data-id="element-987" /> Print
                      </Button>
                      <Button variant="primary" size="sm" onClick={handleDownloadTestPdf} disabled={isGeneratingTestPdf} data-id="element-988">
                        <Download className="w-4 h-4 mr-1" data-id="element-989" />
                        {isGeneratingTestPdf ? 'Generating...' : 'Download PDF'}
                      </Button>
                      <button onClick={closeTestModal} className="p-1.5 hover:bg-gray-100 rounded" aria-label="Close" data-id="element-990">
                        <X className="w-5 h-5 text-gray-500" data-id="element-991" />
                      </button>
                    </div>
                  </div>

                  <div ref={testModalRef} className="p-8 bg-white" data-id="element-992">
                    <div className="flex justify-between items-start mb-6 border-b-2 border-black pb-4" data-id="element-993">
                      <div className="flex items-center" data-id="element-994">
                        <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED" className="h-16 w-16 object-contain mr-3" data-id="element-995" />
                        <div data-id="element-996">
                          <h1 className="text-xl font-bold text-pink-600 uppercase" data-id="element-997">
                            BMED Medical Centre
                          </h1>
                          <p className="text-xs italic text-gray-600" data-id="element-998">
                            Maternity Care Made Better
                          </p>
                        </div>
                      </div>
                      <div className="text-right text-sm" data-id="element-999">
                        <p className="font-bold uppercase border-b border-black pb-1 mb-2" data-id="element-1000">
                          {lab ? 'Laboratory Report' : 'Radiology Report'}
                        </p>
                        <p data-id="element-1001">
                          <span className="font-semibold" data-id="element-1002">Patient:</span>{' '}
                          {patient.firstName} {patient.lastName}
                        </p>
                        <p data-id="element-1003">
                          <span className="font-semibold" data-id="element-1004">File No:</span>{' '}
                          {patient.bmcFileNumber}
                        </p>
                        <p data-id="element-1005">
                          <span className="font-semibold" data-id="element-1006">Age/Sex:</span>{' '}
                          {patient.age} / {patient.sex}
                        </p>
                        <p data-id="element-1007">
                          <span className="font-semibold" data-id="element-1008">Date:</span>{' '}
                          {new Date((lab?.completedAt || rad?.completedAt) ?? Date.now()).toLocaleString()}
                        </p>
                      </div>
                    </div>

                    {lab && <div data-id="element-1009">
                        <h2 className="text-lg font-bold mb-3 uppercase" data-id="element-1010">
                          {focusedTest || 'Tests Performed'}
                        </h2>

                        {/* ─── Structured FBC Form ─── */}
                        {focusedTest && isFBCTest(focusedTest) ? <div className="space-y-4" data-id="element-1011">
                            <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-1012">
                              <h3 className="font-bold uppercase" data-id="element-1013">
                                Full Blood Count (FBC) Report
                              </h3>
                            </div>
                            {fbcStructure.map(cat => <div key={cat.category} className="border border-black" data-id="element-1014">
                                <div className="bg-gray-100 p-2 font-bold border-b border-black text-xs uppercase" data-id="element-1015">
                                  {cat.category}
                                </div>
                                <table className="w-full border-collapse text-sm" data-id="element-1016">
                                  <thead data-id="element-1017">
                                    <tr className="bg-gray-50" data-id="element-1018">
                                      <th className="border border-black p-1.5 text-left" data-id="element-1019">
                                        Test
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-32" data-id="element-1020">
                                        Result
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-32" data-id="element-1021">
                                        Reference Range
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-24" data-id="element-1022">
                                        Unit
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody data-id="element-1023">
                                    {cat.tests.map(t => <tr key={t.name} data-id="element-1024">
                                        <td className="border border-black p-1.5" data-id="element-1025">
                                          {t.name}
                                        </td>
                                        <td className="border border-black p-1.5 font-bold" data-id="element-1026">
                                          {lab.results && lab.results[t.name] || '—'}
                                        </td>
                                        <td className="border border-black p-1.5 text-xs" data-id="element-1027">
                                          {t.range}
                                        </td>
                                        <td className="border border-black p-1.5 text-xs" data-id="element-1028">
                                          {t.unit}
                                        </td>
                                      </tr>)}
                                  </tbody>
                                </table>
                              </div>)}
                          </div> : focusedTest && isUrinalysisTest(focusedTest) ? <div data-id="element-1029">
                            <div className="text-center border-b-2 border-black pb-2 mb-6" data-id="element-1030">
                              <h3 className="font-bold uppercase" data-id="element-1031">
                                Urinalysis Report
                              </h3>
                            </div>
                            <div className="max-w-md mx-auto space-y-2" data-id="element-1032">
                              {urinalysisTests.map(t => {
                      const key = `UA_${t.name}`;
                      const value = lab.results && (lab.results[key] || lab.results[t.name]) || '';
                      return <div key={t.name} className="flex items-center text-sm" data-id="element-1033">
                                    <span className="w-16 font-bold" data-id="element-1034">
                                      {t.name}
                                    </span>
                                    <span className="mx-3 text-gray-400" data-id="element-1035">
                                      -
                                    </span>
                                    <span className="flex-1 border-b border-gray-400 pb-0.5 font-medium" data-id="element-1036">
                                      {value || '\u00A0'}
                                    </span>
                                  </div>;
                    })}
                            </div>
                            <div className="max-w-md mx-auto mt-10 grid grid-cols-2 gap-4 text-sm" data-id="element-1037">
                              <div data-id="element-1038">
                                <p className="font-semibold mb-2" data-id="element-1039">
                                  Dr.'s Name:
                                </p>
                                <p className="border-b border-black pb-0.5 min-h-[1.25rem]" data-id="element-1040">
                                  {lab.results && lab.results['UA_DR_NAME'] || '\u00A0'}
                                </p>
                              </div>
                              <div data-id="element-1041">
                                <p className="font-semibold mb-2" data-id="element-1042">Signature:</p>
                                <p className="border-b border-black pb-0.5 min-h-[1.25rem]" data-id="element-1043">
                                  {lab.results && lab.results['UA_DR_SIG'] || '\u00A0'}
                                </p>
                              </div>
                            </div>
                          </div> : focusedTest && isTestosteroneTest(focusedTest) ? <div className="space-y-2" data-id="element-1044">
                            <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-1045">
                              <h3 className="font-bold uppercase" data-id="element-1046">
                                Testosterone Report
                              </h3>
                            </div>
                            <table className="w-full border-collapse text-sm" data-id="element-1047">
                              <thead data-id="element-1048">
                                <tr className="bg-gray-100" data-id="element-1049">
                                  <th className="border border-black p-1.5 text-left" data-id="element-1050">
                                    Test
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-32" data-id="element-1051">
                                    Result
                                  </th>
                                  <th className="border border-black p-1.5 text-left" data-id="element-1052">
                                    Reference (Male)
                                  </th>
                                  <th className="border border-black p-1.5 text-left" data-id="element-1053">
                                    Reference (Female)
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-20" data-id="element-1054">
                                    Unit
                                  </th>
                                </tr>
                              </thead>
                              <tbody data-id="element-1055">
                                {testosteroneTests.map(t => <tr key={t.name} data-id="element-1056">
                                    <td className="border border-black p-1.5" data-id="element-1057">
                                      {t.name}
                                    </td>
                                    <td className="border border-black p-1.5 font-bold" data-id="element-1058">
                                      {lab.results && (lab.results[`TESTO_${t.name}`] || lab.results[t.name]) || '—'}
                                    </td>
                                    <td className="border border-black p-1.5 text-xs" data-id="element-1059">
                                      {t.rangeMale}
                                    </td>
                                    <td className="border border-black p-1.5 text-xs" data-id="element-1060">
                                      {t.rangeFemale}
                                    </td>
                                    <td className="border border-black p-1.5 text-xs" data-id="element-1061">
                                      {t.unit}
                                    </td>
                                  </tr>)}
                              </tbody>
                            </table>
                          </div> : labResultsFiltered && Object.keys(labResultsFiltered).length > 0 ? <table className="w-full border-collapse text-sm" data-id="element-1062">
                            <thead data-id="element-1063">
                              <tr className="bg-gray-100" data-id="element-1064">
                                <th className="border border-black p-2 text-left" data-id="element-1065">
                                  Parameter
                                </th>
                                <th className="border border-black p-2 text-left" data-id="element-1066">
                                  Result
                                </th>
                              </tr>
                            </thead>
                            <tbody data-id="element-1067">
                              {Object.entries(labResultsFiltered).map(([k, v]) => <tr key={k} data-id="element-1068">
                                    <td className="border border-black p-2 font-medium" data-id="element-1069">
                                      {k}
                                    </td>
                                    <td className="border border-black p-2" data-id="element-1070">
                                      {v}
                                    </td>
                                  </tr>)}
                            </tbody>
                          </table> : <p className="text-gray-500 italic" data-id="element-1071">
                            No structured results recorded for this test.
                          </p>}
                      </div>}

                    {rad && <div data-id="element-1072">
                        <h2 className="text-lg font-bold mb-3 uppercase" data-id="element-1073">
                          {rad.scanType}
                        </h2>
                        {rad.radiographerName && <p className="text-sm mb-3" data-id="element-1074">
                            <span className="font-semibold" data-id="element-1075">
                              Sonographer / Radiographer:
                            </span>{' '}
                            {rad.radiographerName}
                          </p>}
                        {rad.report && <div className="mb-4 border border-black p-4" data-id="element-1076">
                            <pre className="whitespace-pre-wrap font-sans text-sm" data-id="element-1077">
                              {rad.report}
                            </pre>
                          </div>}
                        {rad.images && rad.images.length > 0 && <div data-id="element-1078">
                            <div className="flex justify-between items-center mb-3 print:hidden" data-id="element-1079">
                              <h3 className="font-bold" data-id="element-1080">Scan Images</h3>
                              <div className="flex items-center space-x-2 bg-gray-100 px-3 py-1.5 rounded-lg" data-id="element-1081">
                                <button onClick={() => setTestImageZoom(z => Math.max(10, z - 10))} className="p-1 hover:bg-gray-200 rounded" aria-label="Zoom out" data-id="element-1082">
                                  <ZoomOut className="w-4 h-4" data-id="element-1083" />
                                </button>
                                <input type="range" min={10} max={200} step={10} value={testImageZoom} onChange={e => setTestImageZoom(Number(e.target.value))} className="w-32" data-id="element-1084" />
                                <button onClick={() => setTestImageZoom(z => Math.min(200, z + 10))} className="p-1 hover:bg-gray-200 rounded" aria-label="Zoom in" data-id="element-1085">
                                  <ZoomIn className="w-4 h-4" data-id="element-1086" />
                                </button>
                                <span className="text-sm font-medium w-12 text-right" data-id="element-1087">
                                  {testImageZoom}%
                                </span>
                              </div>
                            </div>
                            <div className="space-y-4 print:space-y-2" data-id="element-1088">
                              {rad.images.map((img, idx) => <div key={idx} className="border-2 border-gray-300 rounded overflow-auto bg-gray-50 max-h-[600px] print:max-h-none print:overflow-visible" data-id="element-1089">
                                  <div style={{
                        transform: `scale(${testImageZoom / 100})`,
                        transformOrigin: 'top left',
                        display: 'inline-block'
                      }} data-id="element-1090">
                                    <img src={img} alt={`${rad.scanType} ${idx + 1}`} className="block" data-id="element-1091" />
                                  </div>
                                </div>)}
                            </div>
                          </div>}
                      </div>}
                  </div>
                </div>
              </div>;
      })()}
      </div>;
  }
  return <div className="space-y-6" data-id="element-1092">
      <div className="flex justify-between items-center" data-id="element-1093">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1094">
          Clinician Dashboard
        </h1>
        <Badge variant="info" className="text-sm px-3 py-1" data-id="element-1095">
          {pendingEncounters.length} Patients Waiting
        </Badge>
      </div>

      <Card noPadding data-id="element-1096">
        <ul className="divide-y divide-gray-200" data-id="element-1097">
          {pendingEncounters.length > 0 ? pendingEncounters.map(encounter => {
          const patient = getPatient(encounter.patientId);
          if (!patient) return null;
          const isWaitingResults = encounter.stage === 'INVESTIGATIONS';
          const hasResults = isWaitingResults && !encounter.pendingLab && !encounter.pendingRadiology;
          return <li key={encounter.id} className="p-4 hover:bg-gray-50 flex justify-between items-center" data-id="element-1098">
                  <div className="flex items-center" data-id="element-1099">
                    <div className={`p-2 rounded-full mr-4 ${hasResults ? 'bg-green-100' : 'bg-blue-100'}`} data-id="element-1100">
                      <Stethoscope className={`h-6 w-6 ${hasResults ? 'text-green-600' : 'text-blue-600'}`} data-id="element-1101" />
                    </div>
                    <div data-id="element-1102">
                      <p className="text-sm font-medium text-blue-600" data-id="element-1103">
                        {patient.bmcFileNumber}
                      </p>
                      <p className="text-lg font-bold text-gray-900" data-id="element-1104">
                        {patient.firstName} {patient.lastName}
                      </p>
                      <div className="flex space-x-2 mt-1" data-id="element-1105">
                        <Badge variant="default" data-id="element-1106">{patient.age} yrs</Badge>
                        {hasResults && <Badge variant="success" data-id="element-1107">Results Ready</Badge>}
                      </div>
                    </div>
                  </div>
                  <Button onClick={() => handleSelectEncounter(encounter)} data-id="element-1108">
                    Open File
                  </Button>
                </li>;
        }) : <li className="p-12 text-center text-gray-500" data-id="element-1109">
              <Stethoscope className="mx-auto h-12 w-12 text-gray-300 mb-4" data-id="element-1110" />
              <p className="text-lg font-medium text-gray-900" data-id="element-1111">
                No patients waiting
              </p>
            </li>}
        </ul>
      </Card>
    </div>;
};